import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Pencil, Trash2, UserCheck, UserX, Ban } from "lucide-react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import StaffDialog from "@/components/portal/StaffDialog";
import { PageHeader, Spinner, StatusBadge, EmptyState, ConfirmDialog, btnGhost, btnDanger, fmtDateTime } from "@/components/portal/ui";

const FILTERS = [
  { key: "", label: "Tutti" },
  { key: "pending", label: "In attesa" },
  { key: "approved", label: "Approvati" },
  { key: "suspended", label: "Sospesi" },
  { key: "rejected", label: "Rifiutati" },
];

export default function StaffPage({ kind }) {
  const isDoctor = kind === "doctors";
  const label = isDoctor ? "Medici" : "Infermieri";
  const [items, setItems] = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(() => {
    api.get(`/${kind}`, { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, [kind, filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isDoctor) api.get("/specialties").then((r) => setSpecialties(r.data));
  }, [isDoctor]);

  const setStatus = async (p, status) => {
    try {
      await api.put(`/${kind}/${p.id}`, { status });
      toast.success(`${p.first_name} ${p.last_name}: ${status === "approved" ? "approvato" : status === "suspended" ? "sospeso" : "rifiutato"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/${kind}/${toDelete.id}`);
      toast.success("Profilo eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid={`admin-${kind}`}>
      <PageHeader
        eyebrow={label}
        title={isDoctor ? "Medici registrati" : "Infermieri registrati"}
        desc={isDoctor ? "Approva le candidature, assegna le specialità e gestisci i calendari dei medici." : "Approva le candidature degli infermieri della rete territoriale."}
      />

      <div className="mb-5 inline-flex flex-wrap rounded-full bg-white p-1 ring-1 ring-[#5B2C91]/10" data-testid="staff-filter-tabs">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${filter === f.key ? "bg-[#5B2C91] text-white" : "text-[#4A4059] hover:text-[#5B2C91]"}`} data-testid={`staff-filter-${f.key || "all"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState title={`Nessun ${isDoctor ? "medico" : "infermiere"}`} desc="Le nuove registrazioni compariranno qui." testid="staff-empty" />
      ) : (
        <div className="overflow-x-auto rounded-3xl bg-white ring-1 ring-[#5B2C91]/8" data-testid="staff-table">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nome</TableHead>
                <TableHead>Contatti</TableHead>
                {isDoctor && <TableHead>Specialità</TableHead>}
                <TableHead>Iscrizione</TableHead>
                <TableHead>Registrato</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`staff-row-${p.id}`}>
                  <TableCell>
                    <p className="font-semibold">{p.first_name} {p.last_name}</p>
                    {p.city && <p className="text-xs text-[#6B6280]">{p.city}</p>}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm">{p.email}</p>
                    <p className="text-xs text-[#6B6280]">{p.phone}</p>
                  </TableCell>
                  {isDoctor && (
                    <TableCell>
                      <div className="flex max-w-xs flex-wrap gap-1">
                        {(p.specialties || []).map((s) => (
                          <span key={s.id} className="rounded-full bg-[#5B2C91]/10 px-2 py-0.5 text-[11px] font-semibold text-[#5B2C91]">{s.name}</span>
                        ))}
                        {(p.specialties || []).length === 0 && <span className="text-xs text-[#8A809A]">—</span>}
                      </div>
                    </TableCell>
                  )}
                  <TableCell className="text-sm">{p.license_number || <span className="text-[#8A809A]">—</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-[#6B6280]">{fmtDateTime(p.created_at)}</TableCell>
                  <TableCell><StatusBadge status={p.status} testid={`staff-status-${p.id}`} /></TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      {p.status !== "approved" && (
                        <button onClick={() => setStatus(p, "approved")} className={btnGhost} data-testid={`staff-approve-${p.id}`}><UserCheck className="h-3.5 w-3.5" /> Approva</button>
                      )}
                      {p.status === "pending" && (
                        <button onClick={() => setStatus(p, "rejected")} className={btnDanger} data-testid={`staff-reject-${p.id}`}><UserX className="h-3.5 w-3.5" /> Rifiuta</button>
                      )}
                      {p.status === "approved" && (
                        <button onClick={() => setStatus(p, "suspended")} className={btnGhost} data-testid={`staff-suspend-${p.id}`}><Ban className="h-3.5 w-3.5" /> Sospendi</button>
                      )}
                      {isDoctor && (
                        <Link to={`/admin/calendario?doctor=${p.id}`} className={btnGhost} data-testid={`staff-calendar-${p.id}`}><CalendarDays className="h-3.5 w-3.5" /> Calendario</Link>
                      )}
                      <button onClick={() => setEditing(p)} className={btnGhost} aria-label="Modifica" data-testid={`staff-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setToDelete(p)} className={btnDanger} aria-label="Elimina" data-testid={`staff-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <StaffDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} person={editing} kind={isDoctor ? "doctor" : "nurse"} specialties={specialties} onSaved={load} />
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Eliminare il profilo?" desc={toDelete ? `${toDelete.first_name} ${toDelete.last_name} (${toDelete.email}) verrà rimosso definitivamente.` : ""} onConfirm={remove} confirmLabel="Elimina" destructive />
    </div>
  );
}
